<template>
    <div>
        <PaymentMethodButton
            v-if="!isFormVisible"
            :testid="methodConfig.buttonTestId"
            :icon="methodConfig.buttonIcon"
            :label="paymentMethodName"
            :disabled="disabled"
            @click="selectPaymentMethod"
        />
        <transition name="slide" mode="out-in">
            <v-card v-if="isFormVisible" class="primary defaultBorder">
                <v-skeleton-loader v-if="skeleton" type="list-item,list-item,list-item" />

                <!-- STEP 1: Start -->
                <v-form
                    v-else-if="step === 'start'"
                    v-model="formStartValid"
                    :id="`${this.method}CardForm-1`"
                    class="cardForm"
                    :disabled="loading"
                    @submit.prevent="goToNextStep"
                >
                    <v-card>
                        <v-card-title data-testId="start-title"> {{ $t(this.methodConfig.formTitleKey, { methodName: paymentMethodName }) }}</v-card-title>
                        <v-card-text>
                            <label :for="`${this.method}CardNumber`">
                                {{ $t(this.methodConfig.formNumberLabelKey, { methodName: paymentMethodName }) }}
                                <span v-if="!this.data.card.number" style="color: red;">({{ $t("suffix.required") }})</span>
                            </label>
                            <v-text-field
                                :id="`${this.method}CardNumber`"
                                name="number"
                                v-model="data.card.number"
                                :rules="[rules.required]"
                                :label="$t('input.number.placeholder')"
                                :error-messages="formErrors.number"
                                @input="formErrors = {}"
                                :disabled="loading"
                                outlined
                            >
                            </v-text-field>

                            <label :for="`${this.method}CardSerial`">
                                {{ $t(this.methodConfig.formSerialLabelKey) }}
                                <template v-if="!this.data.card.serial">
                                    <span v-if="this.method === 'gift'" style="color: grey; opacity: 0.5;">({{ $t("suffix.optional") }})</span>
                                    <span v-else style="color: red;">({{ $t("suffix.required") }})</span>
                                </template>
                            </label>
                            <v-text-field
                                :id="`${this.method}CardSerial`"
                                name="serial"
                                v-model="data.card.serial"
                                :rules="this.method === 'cashless' ? [rules.required] : []"
                                :label="$t('input.number.placeholder')"
                                :error-messages="formErrors.serial"
                                @input="formErrors = {}"
                                :disabled="loading"
                                outlined
                            >
                            </v-text-field>

                            <CheckBoxTermsConditions :readTerms="data.readTerms" :loading="loading" />

                            <v-row style="padding: 0 10px 10px;" :justify="isMobile ? 'center' : 'end'">
                                <v-btn
                                    class="mt-4 cancelBtn"
                                    color="primary"
                                    x-large
                                    text
                                    @click="cancel"
                                    outlined
                                >
                                    {{$t('btn.cancel')}}
                                </v-btn>

                                <v-btn
                                    class="mt-4 white--text"
                                    type="submit"
                                    color="button"
                                    :disabled="!canGoToNextStep"
                                    :loading="loading"
                                    x-large
                                >
                                    {{  $t('btn.confirm') }}
                                </v-btn>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-form>

                <!-- STEP 2: Summary -->
                <v-form
                    v-else-if="step === 'summary'"
                    v-model="formSummaryValid"
                    :id="`${this.method}CardForm-2`"
                    class="cardForm"
                    :disabled="loading"
                    @submit.prevent="goToNextStep"
                    lazy-validation>
                    <v-card>
                        <v-card-text>
                            <div class="card_title" data-testId="summary-title">{{ $t('card') }} {{ details.data.uid }}</div>
                            <div class="emptySpace">&nbsp;</div>
                            <div class="card_summary">
                                <div>{{ $t('cart.gift.current.balance')}}: </div>
                                <div>{{  $options.filters.currencyWithoutCountry(details.data.balance) }}</div>
                                <div>{{ $t('order.summary')}}: </div>
                                <div>{{  $options.filters.currency(payments.data.balance)}}</div>
                            </div>
                        </v-card-text>
                        <v-card-text>
                            <label :for="`${this.method}CardBalanceToUse`">
                                {{  $t('giftCard.useBalance') }}
                            </label>
                            <v-text-field
                                v-model="data.card.balance"
                                :id="`${this.method}CardBalanceToUse`"
                                name="useBalance"
                                type="number"
                                :prefix="$options.filters.currencySign()"
                                :rules="[rules.required, rules.balance, rules.summary, rules.notNegative, rules.notZero]"
                                :error-messages="formErrors.useBalance"
                                :disabled="loading"
                                outlined
                                @input="formErrors = {}"
                            >
                            </v-text-field>

                            <CheckBoxTermsConditions :readTerms="data.readTerms" :loading="loading" />

                            <div class="g-recaptcha"
                                v-if="enableCaptcha"
                                data-callback="onReCaptchaSuccess"
                                :data-sitekey="$root.websiteConfig.data.gcp_recaptcha"
                                data-action="PAYMENT"
                            ></div>

                            <v-row style="padding: 0 10px 10px;" :justify="isMobile ? 'center' : 'end'">
                                <v-btn
                                    class="mt-4 cancelBtn"
                                    color="primary"
                                    x-large
                                    text
                                    @click="cancel"
                                    outlined
                                >
                                    <span v-text="$t('btn.cancel')"></span>
                                </v-btn>

                                <v-btn
                                    class="mt-4 white--text"
                                    type="submit"
                                    color="button"
                                    :disabled="!canGoToNextStep"
                                    :loading="loading"
                                    x-large
                                >
                                    <span v-text="$t('btn.confirm')"></span>
                                </v-btn>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-form>
            </v-card>
        </transition>
		<PaymentProcessOverlay
			v-if="error && overlayErrorMessage"
			:error.sync="error"
			:errorMessage="overlayErrorMessage"
			@closed="closeOverlay"
		/>
    </div>
</template>

<script>
import {BaseModel, Rules, CartModel, EventBus, PaymentService, PaymentModel } from "@connectngo/sdk";
import CaptchaMixin from "@/mixins/CaptchaMixin";
import WindowWidthMixin from '@/mixins/WindowWidthMixin';
import CheckBoxTermsConditions from '@/components/Inputs/CheckBoxTermsConditions.vue';
import PaymentMethodButton from "@/components/PaymentMethodButton.vue"
import { PAYMENT_METHOD_CONFIGS } from "@/configs/paymentMethods";
import PaymentProcessOverlay from "@/components/PaymentProcessOverlay.vue";

export default {
    name: "PaymentMethod",
    mixins: [CaptchaMixin, WindowWidthMixin],
    components: {
        PaymentMethodButton,
        CheckBoxTermsConditions,
		PaymentProcessOverlay,
    },
    props: {
        method: {
            type: String,
            require: true
        },
        selectedMethod: {
            type: String,
            require: true
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        skeleton: {
            type: Boolean,
            default: false,
        },
        cart: {
            type: CartModel,
            default: () => new CartModel(),
        },
        payments: {
            type: PaymentModel,
            default: () => new PaymentModel(),
        },
        engine: {
            type: BaseModel,
            default: () => new BaseModel(),
        },
        reservation: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        loading: false,
        paying: false,
        success: false,
        error: false,
		overlayErrorMessage: '',
        step: 'start',
        formStartValid: false,
        formSummaryValid: false,
        details: new BaseModel(),
        data: {
            readTerms: false,
            card: {
                number: '',
                serial: '',
                balance: 0,
            },
        },
        formErrors: {},
        rules: {},
        captchaSuccess : false,
    }),
    computed: {
        methodConfig() {
            return PAYMENT_METHOD_CONFIGS.find((config) => config.method === this.method)
        },
        isFormVisible() {
            return this.method === this.selectedMethod
        },
        canGoToNextStep() {
            switch (this.step) {
                case "start":
                    return this.formStartValid && !this.loading;
                case "summary":
                    return this.formSummaryValid && !this.loading && (!this.enableCaptcha || this.captchaSuccess);
            }
            return true;
        },
		paymentMethodName() {
			// Payment method name or payment engine name for givex
			if(this.engine.data.payment_engine?.name === 'givex') {
				const paymentEngineName = this.$options.filters.translatable(
					// Default to payment engine name if i18n not set for payment engine
					this.engine.data.name,
					this.engine.data.name_i18n,
					this.$i18n.locale
				);

				return this.$options.filters.translatable(
					paymentEngineName,
					this.engine.data?.payment_methods[0]?.name_i18n,
					this.$i18n.locale
				)
			} else {
				return this.$t(this.methodConfig.buttonLabelKey)
			}
		}
    },
    methods: {
        selectPaymentMethod() {
            this.$emit("select-method", this.method);
        },
        resetForm() {
            this.step = 'start'
            this.details = new BaseModel(),
            this.data = {
                readTerms: false,
                card: {
                    number: "",
                    serial: "",
                    balance: 0,
                },
            }
        },
        async goToNextStep() {
            switch (this.step) {
                case "start":
                    this.loading = true;

                    try {
                        let details;

                        if(this.method === 'gift' || this.method === 'cashless') {
                            details = await new PaymentService().getCashlessBalance(
                               this.data.card.number,
                               this.data.card.serial,
                               this.engine.data.id,
                               this.cart.data.uuid
                           )
                        } else if (this.method === 'givex') {
                            details = await new PaymentService().getGivexBalance(
                                this.data.card.number,
                                this.data.card.serial,
                                this.engine.data.id,
                                this.cart.data.uuid,
                            )
                        }

                        // Format balance to always be a float.
                        this.details = { ...details, data: { ...details.data, balance: parseFloat(details.data.balance) } }
                        this.step = "summary";

                        // Populate the "amount to use" field if the balance card > amount to pay.
                        if (this.details.data.balance >= parseFloat(this.cart.data.amountToPay)) {
                            this.data.card.balance = parseFloat(this.cart.data.amountToPay).toFixed(2);
                        } else {
                            this.data.card.balance = this.details.data.balance
                        }
                    } catch (error) {
                        this.$handleError(this, error)
                    } finally {
                        this.loading = false
                    }
                    break;
                case "summary":
                    this.pay();
                    break;
            }
        },
        async pay() {
            this.loading = true;
            this.paying = true;

            try {
                if(this.method === 'gift' || this.method === 'cashless') {
                    await new PaymentService().processCashless(
                        this.data.card.number,
                        this.data.card.serial,
                        this.engine.data.id,
                        this.cart.data.uuid,
                        this.data.card.balance
                    )
                } else if (this.method === 'givex') {
                    await new PaymentService().processGivexPayment(
                        this.data.card.number,
                        this.data.card.serial,
                        this.engine.data.id,
                        this.cart.data.uuid,
                        this.data.card.balance,
                        this.$root.websiteConfig.data.pointofsale_id,
                        this.$root.websiteConfig.data.salesgroup_id,
                    )
                }

                const amountRemaining = this.payments.data.balance - parseFloat(this.data.card.balance)
                this.cart.data.amountToPay = amountRemaining;

                if(this.method === 'gift'){
                    this.cart.data.total = this.cart.data.amountToPay;
                }
                if(this.method === 'cashless'){
                    this.cart.data.balance = this.cart.data.amountToPay;
                }

                if (this.reservation) {
                    EventBus.publish('REZA_UPDATED', this.cart);
                } else {
                    EventBus.publish('CART_UPDATED', this.cart);
                }

                this.$snack(this.$t(this.methodConfig.cardAppliedMessageKey, { methodName: this.paymentMethodName } ));

                if (amountRemaining === 0) {
                    const method = {
                        paymentMethod: this.engine.data.payment_methods[0]?.name,
                        amount: this.data.card.balance
                    }
                    this.$emit('paymentMethodCaptured', method);
                    this.$emit('completed');
                } else {
                    this.$emit('partialPaymentCompleted');
                }
            } catch (error) {
                this.error = true;
				const { no_balance_left } = error;

				if(no_balance_left) {
					this.overlayErrorMessage = this.$t('processPayment.noBalanceLeft');
				} else {
					this.$handleError(this, error, this.formErrors);
				}
            } finally {
                this.resetForm()
                this.loading = false;
                this.paying = false;
            }
        },
        cancel(){
            this.$emit("cancel", null);
            this.resetForm()
        },
        onCaptchaSuccess() {
            this.captchaSuccess = true;
        },
		closeOverlay() {
			this.error = false;
			this.overlayErrorMessage = '';
			this.$router.push({ name: 'home' });
		}
    },
    created() {
        this.rules = {
            required: (value) => Rules.required(value) || this.$t("rules.required"),
            balance: (value) => value <= this.details.data.balance || this.$t("rules.balance"),
            summary: (value) => parseFloat(value) <= parseFloat(this.payments.data.balance) || parseFloat(value) === 0 || this.$t("rules.summary"),
            notNegative: (value) => value >= 0 || this.$t("rules.notNegative"),
            notZero: (value) => value > 0 || this.$t("rules.notZero"),
        };
    },
    mounted() {
        if(this.enableCaptcha) {
            this.$on('captcha-success', this.onCaptchaSuccess);
        }
    },
    beforeDestroy () {
        if(this.enableCaptcha) {
            this.$off('captcha-success', this.onCaptchaSuccess);
        }
    },
    watch: {
        step() {
            this.loadCaptcha();
        },
    },
}
</script>

<style lang="scss" scoped>
    .slide-enter-active, .slide-leave-active {
        transition: all 1s ease;
    }

    .slide-enter, .slide-leave-to {
        max-height: 0;
        opacity: 0;
        overflow: hidden;
    }

    .defaultBorder {
        border: 2px solid;
    }

    .cardForm {

        .card{
            &_title {
                font-size: 24px;
                font-weight: bold;
                color: #383838;
            }

            &_summary {
                display: grid;
                grid-template-columns: 1fr 1fr;
                column-gap: 12px;
                row-gap: 2px;
                font-size: 16px;
            }
        }

        .v-card__title,
        label {
            color: #383838;
        }

        .g-recaptcha {
            margin-bottom: 10px;
        }

        button {
            min-width: 150px;
            height: 48px;
            padding: 0 20px;
            text-transform: none;
        }

		@media (max-width: 600px) {
			button {
				width: 100%;
			}
		}

        .v-card__title,
        button,
        label {
            font-weight: 600;
            font-size: 18px;
        }

        button:not(:last-child) {
            margin-right: 10px;
        }

        .cancelBtn {
            border-style: solid;
            border-width: 1px;
            border-color: inherit !important;
        }
    }

	@media (max-width: 600px) {
		.cardForm {
			button:not(:last-child) {
				margin-right: 0;
			}
		}

	}
</style>
